import { Guest } from '../types';
import GuestSection, { SectionHeader } from './GuestSection';

type EventPageProps = {
  guests: Guest[];
  onGuestAdded: (guest: Guest) => void;
  onUnauthorized: () => void;
};

const marginNotes = [
  ['left', '140px', '-4deg', '17px', 'Servus beinand'], ['right', '430px', '3deg', '17px', 'Bierbank statt Stuhl'],
  ['left', '860px', '5deg', '17px', 'Hoibe oder Maß?'], ['right', '1320px', '-3deg', '17px', 'Zum Wohl, Nachbar!'],
  ['left', '1880px', '-6deg', '17px', 'A Maß, bitte'], ['right', '2380px', '4deg', '17px', 'Prost!'],
  ['left', '2960px', '3deg', '17px', 'Bläsmusik an'], ['right', '270px', '-5deg', '16px', 'Krug hoch!'],
  ['left', '640px', '4deg', '16px', 'Bläsmusik marsch'], ['right', '1050px', '-3deg', '17px', 'Radler zählt auch'],
  ['left', '1610px', '-5deg', '18px', 'Wer ko, der ko'], ['right', '2110px', '4deg', '16px', 'Schuachplattler?'],
  ['left', '2650px', '-3deg', '16px', 'Brezn-Nachschub'], ['right', '3020px', '5deg', '18px', 'Griaß di!'],
] as const;

const schedule = [
  ['15:00 Uhr', 'Fassanstich & offizieller Start — „O\'zapft is!“'],
  ['16:00 Uhr', 'Weißwurst essen'],
  ['17:00 Uhr', 'Bier-Pong-Turnier'],
  ['18:00 Uhr', 'Abendessen'],
  ['Open End', 'Musik, gemütlicher Ausklang'],
];

export default function EventPage({ guests, onGuestAdded, onUnauthorized }: EventPageProps) {
  return (
    <div className="site-content">
      <header className="hero">
        <div className="diamond-background hero-diamonds" aria-hidden="true" />
        <div className="hero-content">
          <div className="event-date">Samstag · 10. Oktober 2026</div>
          <h1>Hansi&apos;s<br />Wiesn 2026</h1>
          <div className="hero-note">o&apos;zapft is!</div>
          <div className="ornament ornament-light" aria-hidden="true"><span /><i /><span /></div>
          <p>Dieses Jahr ohne Scheune, ohne Zelt aber dafür in unserer neuen Halle!</p>
        </div>
      </header>
      <DiamondBand />

      <main>
        {marginNotes.map(([side, top, rotation, fontSize, text]) => (
          <span key={`${top}-${text}`} className={`margin-note margin-note-${side}`} style={{ top, transform: `rotate(${rotation})`, fontSize }} aria-hidden="true">{text}</span>
        ))}

        <section>
          <SectionHeader number="01">@HecopanProduktionsHalle</SectionHeader>
          <p className="address">An der Viehleite 30a, 01796 Pirna</p>
          <img className="hall-image" src="/assets/halle.jpg" alt="Die Produktionshalle" />
        </section>

        <section className="decorated-section">
          <span className="watermark watermark-well" aria-hidden="true">Zum Wohl!</span>
          <SectionHeader number="02">Tagesablauf</SectionHeader>
          <div className="timeline">
            {schedule.map(([time, text], index) => (
              <div className="timeline-item" key={time}>
                <span className="timeline-marker" aria-hidden="true" />
                <div className="timeline-time">{time}</div>
                <div className="timeline-description">{text}</div>
                {index === 2 && <div className="title-defence">Titelverteidigung!</div>}
              </div>
            ))}
          </div>
        </section>

        <section className="decorated-section">
          <span className="watermark watermark-food" aria-hidden="true">An Guadn!</span>
          <SectionHeader number="03">Speiseprogramm</SectionHeader>
          <div className="food-grid">
            <FoodCard title="Weißwurst">Mit süßem Senf und Brezn</FoodCard>
            <FoodCard title="Brotzeit mit Obatzda">Würziger Käse-Butter-Aufstrich mit Zwiebeln</FoodCard>
            <FoodCard title="Frittatensuppe">Klare Brühe mit feinen Pfannkuchenstreifen</FoodCard>
            <FoodCard title="Fleisch & Käse vom Grill" note="Mit echten Bayrischen Bratwürsten">Frisch vom Grill, direkt serviert</FoodCard>
          </div>
        </section>

        <GuestSection guests={guests} onGuestAdded={onGuestAdded} onUnauthorized={onUnauthorized} />

        <section className="decorated-section">
          <span className="watermark watermark-prost" aria-hidden="true">Prost!</span>
          <SectionHeader number="05">Dresscode</SectionHeader>
          <div className="dresscode">
            <p>Tracht ist Ehrensache — Lederhose, Dirndl oder zumindest ein Karo-Hemd sind herzlich willkommen. Bequeme Schuhe nicht vergessen, es wird getanzt!</p>
          </div>
        </section>

        <section>
          <div className="powered-by">powerd by:</div>
          <div className="sponsors">
            <img src="/assets/asc.png" alt="ASC" />
            <img src="/assets/hecopan.svg" alt="Hecopan" />
          </div>
        </section>
      </main>

      <DiamondBand />
      <footer>Hansi&apos;s Wiesn · 10. Oktober</footer>
    </div>
  );
}

function FoodCard({ title, children, note }: { title: string; children: React.ReactNode; note?: string }) {
  return (
    <div className="food-card">
      <div className="food-title">{title}</div>
      <div className="food-description">{children}</div>
      {note && <div className="food-note">{note}</div>}
    </div>
  );
}

function DiamondBand() {
  return <div className="diamond-band" aria-hidden="true" />;
}
